import * as React from "react";
import { connect } from "react-redux";
import { ApiClient } from "../../../lib/api";
import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import { RootState } from "../../../store/rootReducer";
import { Row, Col, message, Skeleton, Icon, Table } from "antd";
import styled from "styled-components";
import { ForwardQuickActionButton } from "../../../shared/ForwardQuickActionbutton";
import { RouteComponentProps } from "react-router";
import { Invoice, OrderInvoiceInformation } from "../../../lib/model/Invoice";
import { priceString, numericDate } from "../../../lib/formatters";
import { InvoiceStatus } from "../../../lib/model/InvoiceStatus";

const GreyBox = styled.div`
  background-color: #f7f7f7;
  border: 1px solid #e8e8e8;
  height: 126px;
  width: 100%;
  padding: 17px 22px;
  text-align: center;
`;

const Frame = styled.div`
  border: 1px solid #e8e8e8;
  width: 100%;
  padding: 20px 25px;

  @media only screen and (max-width: 576px) {
    padding: 10px;
  }
`;

const PriceTitleLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #061a32;
`;

const PriceLabel = styled.span`
  font-size: 28px;
  font-weight: 400;
  color: #061a32;
`;

const HintLabel = styled.span`
  font-size: 11px;
  font-weight: 300;
  color: rgba(6, 26, 50, 0.56);
  display: inline-block;
`;

const DataLabel = styled.span`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.29;
  color: #1d1d1b;
`;

const DataLabelBold = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.29;
  color: #1d1d1b;
`;

const StyledTable = styled(Table)`
  @media only screen and (max-width: 576px) {
    .ant-table-thead > tr > th, .ant-table-tbody > tr > td {
      padding: 6px !important;
    }
`;

const client = new ApiClient();

const tableColumns = [
  {
    title: "Auftragsnummer",
    dataIndex: "orderId",
    key: "orderId",
    width: "25%"
  },
  {
    title: "Auftragsdatum",
    dataIndex: "date",
    key: "date",
    width: "25%"
  },
  {
    title: "Nettopreis",
    dataIndex: "price",
    key: "price",
    width: "25%"
  },
  {
    title: "Route",
    dataIndex: "route",
    key: "route",
    width: "25%"
  }
];

const createTableDataFromInvoiceInfo = (
  order: OrderInvoiceInformation
): any => ({
  key: order.orderId,
  orderId: order.orderId,
  date: numericDate(order.startTime),
  price: priceString(order.fullPrice.net),
  route: order.description
});

type State = {
  invoice?: Invoice;
};

const mapStateToProps = (
  state: RootState,
  ownProps: RouteComponentProps<{ invoiceId: string }>
) => ({
  invoiceId: ownProps.match.params.invoiceId
});

const IconDownload: React.FC = props => {
  return <Icon type="download" style={{ color: "#ffffff" }} />;
};

const dispatchProps = {};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

class InvoiceDetailsContainer extends React.Component<Props, State> {
  readonly state: State = {};
  render() {
    const { invoice } = this.state;
    return (
      <MainComponentContainer>
        <Row type="flex">
          <Col span={24}>
            {/* Price and Action Button Section */}
            <Row type="flex" style={{ margin: "20px 15px" }} gutter={30}>
              <Skeleton active loading={invoice === undefined} title={false}>
                {invoice && (
                  <>
                    <Col span={7} xs={12} md={8}>
                      <GreyBox>
                        <PriceTitleLabel>Rechnungssumme</PriceTitleLabel>
                        <br />
                        <PriceLabel>
                          {priceString(invoice.amount.net)}
                        </PriceLabel>
                        <br />
                        {!!invoice.noVAT && invoice.noVAT ? (
                          <HintLabel>umsatzsteuerbefreit</HintLabel>
                        ) : (
                          <HintLabel>zzgl. 19% Mehrwertsteuer</HintLabel>
                        )}
                      </GreyBox>
                    </Col>
                    <Col span={17} xs={12} md={16}>
                      <GreyBox>
                        <Row
                          type="flex"
                          justify="center"
                          align="middle"
                          style={{ height: "100%" }}
                        >
                          <Col xs={24} md={6}>
                            <ForwardQuickActionButton
                              isDisabled={false}
                              icon={IconDownload}
                              title={
                                invoice.invoiceWithWaybillsPDFUrl
                                  ? "Rechnung inklusive Dokumentation herunterladen"
                                  : "Rechnung herunterladen"
                              }
                              href={
                                invoice.invoiceWithWaybillsPDFUrl ||
                                invoice.invoicePDFUrl
                              }
                              target="_blank"
                            />
                          </Col>
                        </Row>
                      </GreyBox>
                    </Col>
                  </>
                )}
              </Skeleton>
            </Row>
          </Col>
          <Col span={24}>
            {/* Detailed Progress Section */}
            <Row type="flex" style={{ margin: "0 30px 30px 30px" }}>
              <Skeleton active loading={invoice === undefined} title={false}>
                {invoice && (
                  <Col span={24}>
                    <Frame>
                      <Row type="flex">
                        <Col span={24} style={{ marginBottom: 37 }}>
                          <DataLabelBold>Rechnung: </DataLabelBold>
                          <DataLabel>{invoice.id}</DataLabel>
                          <br />
                          <DataLabelBold>Rechnungsdatum: </DataLabelBold>
                          <DataLabel>
                            {numericDate(invoice.createdAt)}
                          </DataLabel>
                          <br />
                          <DataLabelBold>Status: </DataLabelBold>
                          <DataLabel>
                            {invoice.shipperStatus.status === InvoiceStatus.Open
                              ? "offen"
                              : "bezahlt"}
                          </DataLabel>
                        </Col>
                      </Row>
                      <Row type="flex">
                        <Col
                          span={24}
                          style={{ marginBottom: 37, overflow: "auto" }}
                        >
                          <StyledTable
                            dataSource={invoice.orders.map(
                              createTableDataFromInvoiceInfo
                            )}
                            columns={tableColumns}
                            pagination={false}
                          />
                        </Col>
                      </Row>
                    </Frame>
                  </Col>
                )}
              </Skeleton>
            </Row>
          </Col>
        </Row>
      </MainComponentContainer>
    );
  }
  componentDidMount = () => {
    this.fetchInvoice();
  };
  fetchInvoice = async () => {
    try {
      const invoice = await client.fetchInvoice(this.props.invoiceId);
      this.setState({ invoice });
    } catch (error) {
      message.error(error.message);
    }
  };
}

export default connect(mapStateToProps, dispatchProps)(InvoiceDetailsContainer);
